import React from 'react';
import { useTranslation } from 'react-i18next';
import { Button as PrimeButton } from 'primereact/button';
import { Chip } from 'primereact/chip';
import cn from 'classnames';
import moment from 'moment';
import styles from '../TableFilters/TableFilters.styles.scss';

const ColumnFilterChipsDisplay = ({
  filters,
  columns,
  onClearFilter,
  onClearAllFilters,
}) => {
  const { t } = useTranslation();

  if (!filters || !columns) {
    return null;
  }

  const columnDefs = typeof columns === 'function' ? columns({ t }) : columns;
  const columnMap = {};
  columnDefs.forEach((col) => {
    if (col && (col.value || col.filterField)) {
      const fieldName = col.filterField || col.value;
      columnMap[fieldName] = col;
    }
  });

  const activeFilters = [];

  Object.entries(filters).forEach(([field, filterMeta]) => {
    // Skip global filter
    if (field === 'global' || !filterMeta) {
      return;
    }

    const column = columnMap[field];
    if (!column) {
      return;
    }

    const columnLabel = column.label ? t(column.label) : field;

    // Handle constraint-based filters (AND/OR operators)
    if (Array.isArray(filterMeta.constraints)) {
      filterMeta.constraints.forEach((constraint, index) => {
        if (constraint && hasValidFilterValue(constraint.value)) {
          const filterValue = formatFilterValue(
            constraint.value,
            constraint.matchMode,
            column,
          );
          if (filterValue) {
            activeFilters.push({
              field,
              label: columnLabel,
              value: filterValue,
              constraintIndex: index,
            });
          }
        }
      });
    }
    // Handle simple value-based filters
    else if (hasValidFilterValue(filterMeta.value)) {
      const filterValue = formatFilterValue(
        filterMeta.value,
        filterMeta.matchMode,
        column,
      );
      if (filterValue) {
        activeFilters.push({
          field,
          label: columnLabel,
          value: filterValue,
          constraintIndex: null,
        });
      }
    }
  });

  if (activeFilters.length === 0) {
    return null;
  }

  return (
    <div className="d-flex gap-3 flex-wrap mb-3">
      {activeFilters.map((filter, index) => (
        <Chip
          key={`${filter.field}-${index}`}
          style={{
            color: 'black',
            padding: '8px',
            borderRadius: '8px',
            background: 'rgba(207, 219, 221, 0.59)',
            textTransform: 'capitalize',
          }}
          removeIcon="pi pi-times"
          label={
            <label
              style={{
                maxWidth: '380px',
                textOverflow: 'ellipsis',
                overflow: 'hidden',
                display: 'block',
              }}
            >
              <b>{filter.label}: </b>
              {filter.value}
            </label>
          }
          removable
          onRemove={() => onClearFilter(filter.field, filter.constraintIndex)}
        />
      ))}
      {activeFilters.length > 0 && (
        <PrimeButton
          onClick={onClearAllFilters}
          type="button"
          className={cn(styles.clearAllButton, 'text-decoration-underline')}
          label={t('common.clear_all')}
          link
        />
      )}
    </div>
  );
};

/**
 * Check if a filter value is valid (not null, undefined, or empty string)
 */
function hasValidFilterValue(value) {
  if (value === null || value === undefined || value === '') {
    return false;
  }
  // Handle empty arrays
  if (Array.isArray(value) && value.length === 0) {
    return false;
  }
  // Handle empty objects (but allow date objects)
  if (
    typeof value === 'object' &&
    !Array.isArray(value) &&
    !(value instanceof Date)
  ) {
    if (Object.keys(value).length === 0) {
      return false;
    }
    // Check for date range objects
    if ('from' in value || 'to' in value) {
      return value.from || value.to;
    }
  }
  return true;
}

/**
 * Format filter value for display in chip
 */
function formatFilterValue(value, matchMode, column) {
  // Use custom formatter if provided
  if (
    column?.filterValueFormatter &&
    typeof column.filterValueFormatter === 'function'
  ) {
    return column.filterValueFormatter(value);
  }

  // Handle array values
  if (Array.isArray(value)) {
    if (value.length === 0) {
      return null;
    }

    // Check if this is a date range array [startDate, endDate]
    if (
      value.length === 2 &&
      (value[0] instanceof Date || value[1] instanceof Date)
    ) {
      // Determine format based on column type
      let format = 'DD MMM YYYY'; // default date format
      if (column?.dataType === 'time') {
        format = 'hh:mm:ss A';
      } else if (column?.dataType === 'datetime') {
        format = 'DD MMM YYYY hh:mm:ss A';
      }

      const from = value[0] ? moment(value[0]).format(format) : '';
      const to = value[1] ? moment(value[1]).format(format) : '';
      if (from && to) {
        return `${from} - ${to}`;
      }
      return from || to;
    }

    // Check if items have labels (for multi-select)
    if (value[0] && typeof value[0] === 'object' && 'label' in value[0]) {
      return value.map((v) => v.label).join(', ');
    }

    return value.join(', ');
  }

  // Handle object values with label property
  if (value && typeof value === 'object') {
    if ('label' in value) {
      return value.label;
    }
    // Handle date range objects (legacy support)
    if ('from' in value && 'to' in value) {
      // Determine format based on column type
      let format = 'DD MMM YYYY'; // default date format
      if (column?.dataType === 'time') {
        format = 'hh:mm:ss A';
      } else if (column?.dataType === 'datetime') {
        format = 'DD MMM YYYY hh:mm:ss A';
      }

      const from = value.from ? moment(value.from).format(format) : '';
      const to = value.to ? moment(value.to).format(format) : '';
      if (from && to) {
        return `${from} - ${to}`;
      }
      return from || to;
    }
  }

  // Handle date/time values based on column type
  if (column?.dataType || column?.isDateColumn) {
    const momentValue = moment(value, moment.ISO_8601, true).isValid()
      ? moment(value)
      : moment(value, 'YYYY-MM-DD hh:mm:ss', true).isValid()
      ? moment(value, 'YYYY-MM-DD hh:mm:ss')
      : null;

    if (momentValue) {
      // Check column data type
      switch (column.dataType) {
        case 'date':
          return momentValue.format('DD MMM YYYY');
        case 'time':
          return momentValue.format('hh:mm:ss A');
        case 'datetime':
          return momentValue.format('DD MMM YYYY hh:mm:ss A');
        default:
          // Legacy support for isDateColumn
          if (column?.isDateColumn) {
            return momentValue.format('DD MMM YYYY');
          }
      }
    }
  }

  // Handle boolean values
  if (typeof value === 'boolean') {
    return value ? 'Yes' : 'No';
  }

  // Default: return as string
  return String(value);
}

export default React.memo(ColumnFilterChipsDisplay);
